/**
 * Guided tour of the live interface: a spotlight (one element whose huge box-shadow dims everything
 * else) moves between the real controls while a small card explains each one in a single line.
 * Keyboard: arrows / Enter to move, Esc to leave. Steps whose target is hidden (mobile, open sheet)
 * are skipped automatically.
 */
import { $, esc, icon, isMobile } from './ui.js';

const STEPS = [
  { sel: ['#search-card'], pref: 'bottom', title: 'Tìm kiếm', text: 'Gõ địa chỉ, dán toạ độ WGS84, VN-2000, Plus Code, link Google Maps hoặc mã đồ án.' },
  { map: true, pref: 'bottom', title: 'Nhấp vào bản đồ', text: 'Xem ngay chức năng sử dụng đất, chỉ tiêu ô đất và đồ án tại điểm đó.' },
  { sel: ['#layer-chip'], pref: 'top', title: 'Lớp quy hoạch', text: 'Chuyển giữa quy hoạch chi tiết, phân khu, giao thông, quy hoạch chung.', key: 'P' },
  { sel: ['#dock-bm'], pref: 'top', title: 'Bản đồ nền', text: 'Xem trước rồi chọn nền sáng, tối, vệ tinh hoặc vệ tinh kèm nhãn.', key: 'B' },
  { sel: ['#btn-catalog'], pref: 'left', title: 'Danh mục đồ án', text: 'Mọi đồ án quy hoạch của Hà Nội: tìm theo mã, tên, số quyết định; bấm để bay tới ranh giới. Đồ án chưa số hoá có quyết định và bản vẽ công bố.', key: 'D' },
  { sel: ['#controls .ctl-group:first-child'], pref: 'left', title: 'Công cụ', text: 'Chú giải, kiểm tra khu đất, đo khoảng cách, diện tích, so sánh hai lớp.' },
  { sel: ['#btn-help', '#btn-help-m'], pref: 'bottom', title: 'Xem lại hướng dẫn', text: 'Demo thao tác và phím tắt luôn ở đây.', key: '?' },
];

let root = null;
let steps = [];
let idx = 0;
let lastFocus = null;
let raf = 0;

const shown = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return false;
  if (el.checkVisibility) return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  return getComputedStyle(el).visibility !== 'hidden';
};
const target = (s) => (s.map ? null : s.sel.map((q) => $(q)).find(shown) || null);

export const isTourOpen = () => !!root;

export function startTour() {
  endTour(false);
  steps = STEPS.filter((s) => s.map || target(s));
  if (!steps.length) return;
  idx = 0;
  lastFocus = document.activeElement;
  root = document.createElement('div');
  root.className = 'tour';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'tour-title');
  root.innerHTML = `
    <div class="tour-spot" aria-hidden="true"></div>
    <div class="tour-card glass" aria-live="polite">
      <div class="tour-top"><span class="tour-step" id="tour-step"></span><button type="button" class="icon-btn ghost sm" data-act="skip" aria-label="Kết thúc tham quan">${icon('x')}</button></div>
      <h3 id="tour-title"></h3>
      <p id="tour-text"></p>
      <div class="tour-foot">
        <div class="tour-dots" aria-hidden="true">${steps.map(() => '<i></i>').join('')}</div>
        <div class="tour-btns">
          <button type="button" class="btn sm" data-act="prev">${icon('arrow-left')}Trước</button>
          <button type="button" class="btn sm primary" data-act="next"></button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(root);
  document.body.classList.add('touring');
  root.addEventListener('click', onClick);
  addEventListener('keydown', onKey, true);
  addEventListener('resize', schedule);
  show(0, true);
}

export function endTour(done = true) {
  if (!root) return;
  removeEventListener('keydown', onKey, true);
  removeEventListener('resize', schedule);
  cancelAnimationFrame(raf);
  root.remove();
  root = null;
  document.body.classList.remove('touring');
  if (done) { try { localStorage.setItem('qh-tour', 'done'); } catch { /* private mode */ } }
  const back = lastFocus && document.contains(lastFocus) ? lastFocus : $('#search-input');
  back?.focus?.({ preventScroll: true });
}

function show(i, first = false) {
  idx = Math.max(0, Math.min(steps.length - 1, i));
  const s = steps[idx];
  const last = idx === steps.length - 1;
  $('#tour-step', root).textContent = `Bước ${idx + 1} / ${steps.length}`;
  $('#tour-title', root).textContent = s.title;
  $('#tour-text', root).innerHTML = esc(s.text) + (s.key && !isMobile() ? ` <span class="tour-key">Phím <kbd class="kc">${esc(s.key)}</kbd></span>` : '');
  root.querySelectorAll('.tour-dots i').forEach((d, k) => d.classList.toggle('on', k === idx));
  const prev = root.querySelector('[data-act=prev]');
  prev.hidden = idx === 0;
  const next = root.querySelector('[data-act=next]');
  next.innerHTML = last ? `Xong${icon('check')}` : `Tiếp${icon('arrow-right')}`;
  const card = root.querySelector('.tour-card');
  card.classList.remove('swap');
  void card.offsetWidth;
  card.classList.add('swap');
  layout(first);
  next.focus({ preventScroll: true });
}

function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => layout(false)); }

/** Spotlight rectangle for the current step (viewport px). */
function spotRect(s) {
  if (s.map) {
    const vw = innerWidth, vh = innerHeight;
    const left = isMobile() ? 0 : ($('#left-col')?.getBoundingClientRect().right || 0);
    const right = $('#controls')?.getBoundingClientRect().left || vw;
    const d = Math.min(180, vw * 0.42);
    const cx = (left + right) / 2, cy = isMobile() ? vh * 0.5 : vh * 0.46;
    return { left: cx - d / 2, top: cy - d / 2, width: d, height: d, round: true };
  }
  const r = target(s)?.getBoundingClientRect();
  if (!r) return { left: innerWidth / 2, top: innerHeight / 2, width: 0, height: 0 };
  const pad = 6;
  return { left: r.left - pad, top: r.top - pad, width: r.width + 2 * pad, height: r.height + 2 * pad };
}

function layout(first) {
  if (!root) return;
  const s = steps[idx];
  const r = spotRect(s);
  const spot = root.querySelector('.tour-spot');
  const card = root.querySelector('.tour-card');
  if (first) { spot.style.transition = 'none'; card.style.transition = 'none'; }
  Object.assign(spot.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, borderRadius: r.round ? '50%' : '16px' });

  const vw = innerWidth, vh = innerHeight, m = 12, gap = 14;
  const cw = card.offsetWidth, ch = card.offsetHeight;
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  const clampX = (x) => Math.max(m, Math.min(vw - cw - m, x));
  const clampY = (y) => Math.max(m, Math.min(vh - ch - m, y));
  const cand = {
    bottom: () => (r.top + r.height + gap + ch <= vh - m ? [clampX(cx - cw / 2), r.top + r.height + gap] : null),
    top: () => (r.top - gap - ch >= m ? [clampX(cx - cw / 2), r.top - gap - ch] : null),
    left: () => (r.left - gap - cw >= m ? [r.left - gap - cw, clampY(cy - ch / 2)] : null),
    right: () => (r.left + r.width + gap + cw <= vw - m ? [r.left + r.width + gap, clampY(cy - ch / 2)] : null),
  };
  const order = [s.pref, 'bottom', 'top', 'left', 'right'];
  let pos = null;
  for (const k of order) { pos = cand[k]?.(); if (pos) break; }
  if (!pos) pos = [clampX(vw / 2 - cw / 2), vh - ch - m];
  card.style.left = `${Math.round(pos[0])}px`;
  card.style.top = `${Math.round(pos[1])}px`;
  if (first) { void card.offsetWidth; spot.style.transition = ''; card.style.transition = ''; }
}

function onClick(e) {
  const b = e.target.closest('[data-act]');
  if (!b) return;
  if (b.dataset.act === 'skip') endTour(true);
  else if (b.dataset.act === 'prev') show(idx - 1);
  else if (idx >= steps.length - 1) endTour(true);
  else show(idx + 1);
}

function onKey(e) {
  if (!root) return;
  if (e.key === 'Tab') {
    // keep focus inside the card
    const f = [...root.querySelectorAll('button:not([hidden])')];
    const at = f.indexOf(document.activeElement);
    e.preventDefault();
    f[(at + (e.shiftKey ? f.length - 1 : 1)) % f.length]?.focus();
    return;
  }
  e.stopImmediatePropagation();
  if (e.key === 'Escape') { e.preventDefault(); endTour(true); }
  else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); if (idx < steps.length - 1) show(idx + 1); }
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); if (idx > 0) show(idx - 1); }
}
